import { API_BASE } from './http';
import { withCsrfHeader } from '../csrf';

export interface PageContextPayload {
  primary?: { type: string; id: string; label?: string; metadata?: Record<string, unknown> };
  recent?: Array<{ type: string; id: string; label: string; visitedAt?: number }>;
  pinned?: Array<{ type: string; id: string; label?: string }>;
}

export interface StreamDoneMeta {
  messageId: string;
  durationMs: number;
  model: string;
  inputTokens?: number;
  outputTokens?: number;
  costEur?: number;
  tier?: string;
  /** Von KIAN vorbereitete Entwürfe — erscheinen als Karten unter der Antwort */
  actionIds?: string[];
  /** Gesprächsmodus: Antwort wurde vorgelesen */
  voice?: boolean;
  /** Gesprächsmodus: angehängtes Vorlesen eines Entwurfs — per „Ja, anlegen" bestätigbar? */
  voiceDraft?: { actionId: string; verb: 'anlegen' | 'ausführen'; confirmable: boolean };
  /** Das Werkzeug-Budget hat für diese Antwort nicht gereicht (Budget, mit dem sie lief) */
  toolBudgetExhausted?: boolean;
  toolBudget?: number;
}

const NOT_RECEIVED = 'Deine Nachricht ist nicht bei KIAN angekommen — bitte gleich noch einmal senden.';

/**
 * Lesbare Meldung zu einer Fehlantwort. Bis v1.388.x stand hier der rohe Antworttext — bei einem
 * Deploy also Cloudflares HTML-Seite „502: Bad gateway" als Fehlermeldung im Chat.
 */
export function streamErrorMessage(status: number, raw: string, contentType: string | null): string {
  const text = raw.trim();
  const html = (contentType ?? '').toLowerCase().includes('text/html') || /^<(!doctype|html)/i.test(text);
  if (status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 530)) {
    return `ServiceHub startet gerade neu (HTTP ${status}). ${NOT_RECEIVED}`;
  }
  if (!html && text) {
    try {
      const parsed = JSON.parse(text) as { error?: unknown; message?: unknown };
      const msg = typeof parsed.error === 'string' ? parsed.error : typeof parsed.message === 'string' ? parsed.message : null;
      if (msg?.trim()) return msg.trim().slice(0, 400);
    } catch {
      return text.slice(0, 400);
    }
  }
  return `KIAN konnte nicht antworten (HTTP ${status}). ${NOT_RECEIVED}`;
}

export async function postAssistantStream(
  sessionId: string,
  content: string,
  pageContext: PageContextPayload | undefined,
  hints: Record<string, unknown> | undefined,
  handlers: {
    onDelta: (text: string) => void;
    onDone: (meta: StreamDoneMeta) => void;
    onError: (msg: string) => void;
  },
  signal?: AbortSignal
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/assistant/session/${sessionId}/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: withCsrfHeader('POST', { 'Content-Type': 'application/json' }),
      body: JSON.stringify({ content, pageContext, hints }),
      signal,
    });
  } catch (err) {
    // Abbruch durch den Nutzer bleibt ein AbortError — der Aufrufer lässt die Teil-Antwort stehen.
    if (err instanceof Error && err.name === 'AbortError') throw err;
    handlers.onError(`ServiceHub nicht erreichbar. ${NOT_RECEIVED}`);
    return;
  }

  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    handlers.onError(streamErrorMessage(res.status, raw, res.headers.get('content-type')));
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    handlers.onError('Kein Response-Stream');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const processBlocks = (text: string) => {
    const blocks = text.split('\n\n');
    for (const block of blocks) {
      if (!block.trim()) continue;
      let event = '';
      let dataLine = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) dataLine = line.slice(5).trim();
      }
      if (!dataLine) continue;
      try {
        const data = JSON.parse(dataLine) as Record<string, unknown>;
        if (event === 'delta' && typeof data.text === 'string') {
          handlers.onDelta(data.text);
        }
        if (event === 'done') {
          handlers.onDone(data as unknown as StreamDoneMeta);
        }
        if (event === 'error' && typeof data.error === 'string') {
          handlers.onError(data.error);
        }
      } catch {
        /* ignore */
      }
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lastSep = buffer.lastIndexOf('\n\n');
      if (lastSep >= 0) {
        processBlocks(buffer.slice(0, lastSep));
        buffer = buffer.slice(lastSep + 2);
      }
    }
  } catch (err) {
    // Network interruption or reader failure — surface to caller unless already aborted
    if (signal?.aborted) return;
    handlers.onError('Stream unterbrochen: ' + (err instanceof Error ? err.message : String(err)));
    return;
  }

  // Flush any remaining buffered data (handles incomplete final block gracefully)
  if (buffer.trim()) {
    try {
      processBlocks(buffer);
    } catch {
      // Malformed final block — stream is done, no error needed
    }
  }
}
